/**
 * Employee entity: backend DTOs, API calls, and the DTO -> display mapper.
 */

import type { Employee } from '../../app/types';
import { apiGet, apiPost, apiPut } from '../apiClient';
import type { ChecklistSummary } from './checklist';
import { contractTypeToDisplay, formatDate } from './format';

/** Backend contract-type enum (camelCase on the wire). */
export type ContractType = 'fullTime' | 'partTime' | 'casual';

/**
 * One item on an employee's checklist as the backend returns it.
 *
 * NOTE: `checks` is EMPTY on the current surface — it is populated later by
 * platform (assessment) resolution. This shape is a best-effort mirror of the
 * display EmployeeCheck so the mapper is ready when data arrives; treat every
 * field as provisional until that phase lands.
 */
export interface EmployeeCheckDto {
  id: string;
  name: string;
  kind: 'document' | 'assertion';
  category: string;
  status: string;
  note?: string | null;
  date?: string | null;
}

export interface EmployeeDto {
  id: string;
  companyId: string;
  name: string;
  role: string;
  roleTags: string[];
  contractType: ContractType;
  startedOn: string;
  endedOn: string | null;
  note: string | null;
  /** Captured per-hire predicate answers (slug -> yes/no); empty until the questions are asked. */
  predicates: Record<string, boolean>;
  /** This person's own resolved checklist summary; populated by the list endpoint (zeros elsewhere). */
  checklist: ChecklistSummary;
  createdAt: string;
}

export interface CreateEmployeeRequest {
  companyId: string;
  name: string;
  role?: string;
  roleTags?: string[];
  contractType: ContractType;
  /** "yyyy-MM-dd". */
  startedOn: string;
  note?: string;
}

export interface UpdateEmployeeRequest {
  name: string;
  role?: string;
  contractType: ContractType;
  note?: string;
  /** "yyyy-MM-dd" — set to offboard the employee. */
  endedOn?: string;
  /**
   * Replaces the captured per-hire answers. Omit to leave them untouched: the offboard path must omit
   * it, or offboarding would wipe the answers it never loaded.
   */
  predicates?: Record<string, boolean>;
}

/** GET /api/employees?companyId={id}. */
export function listEmployees(companyId: string): Promise<EmployeeDto[]> {
  return apiGet<EmployeeDto[]>(`/api/employees?companyId=${encodeURIComponent(companyId)}`);
}

/** GET /api/employees/{id}. */
export function getEmployee(id: string): Promise<EmployeeDto> {
  return apiGet<EmployeeDto>(`/api/employees/${id}`);
}

/** POST /api/employees. */
export function createEmployee(request: CreateEmployeeRequest): Promise<EmployeeDto> {
  return apiPost<EmployeeDto>('/api/employees', request);
}

/** PUT /api/employees/{id} — edit or offboard (via endedOn). */
export function updateEmployee(id: string, request: UpdateEmployeeRequest): Promise<EmployeeDto> {
  return apiPut<EmployeeDto>(`/api/employees/${id}`, request);
}

/** EmployeeDto -> display Employee. */
export function mapEmployee(dto: EmployeeDto): Employee {
  const needed = dto.checklist?.needed ?? 0;
  return {
    id: dto.id,
    name: dto.name,
    role: dto.role,
    contractType: contractTypeToDisplay(dto.contractType),
    started: dto.startedOn ? `Started ${formatDate(dto.startedOn)}` : '',
    note: dto.note ?? undefined,
    // The person's own resolved checklist, driven by their predicate answers (list endpoint only).
    checklist: {
      needed,
      outstanding: dto.checklist?.missing ?? 0,
      needsAnswer: dto.checklist?.needsAnswer ?? 0,
    },
  };
}
